/**
 * usePlayBlocks Hook
 *
 * Core hook for managing play state with React Query.
 * Replaces PlayContext with server-state management.
 *
 * Phase 1: Core infrastructure
 * - React Query for server state
 * - Local UI state (drawingState, hashAlignment)
 * - Placeholder mutations and undo/redo
 *
 * Phase 3: Adapter Layer Integration
 * - Uses real adapters for block-to-canvas type conversion
 */

import { useState, useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ResolvedBlock, PlayerBlock } from '@/types/blocks'
import { getResolvedBlockQueryKey, fetchResolvedBlock } from '@/hooks/UseResolvedBlock'
import type { DrawingState, HashAlignment } from './Types'
import type {
	UsePlayBlocksOptions,
	UsePlayBlocksReturn,
} from './Types'
import {
	DEFAULT_DRAWING_STATE,
	DEFAULT_HASH_ALIGNMENT,
} from './Types'
import { playerBlocksToPlayers } from '@/adapters/PlayerAdapter'
import { drawingBlocksToDrawings } from '@/adapters/DrawingAdapter'
import { usePlayMutations } from './UsePlayMutations'
import { useUndo } from './undo'

/**
 * Transform PlayerBlock[] to Player[] for canvas rendering.
 * Uses the adapter layer for proper type conversion.
 * Filters out invalid entries before transformation.
 */
function transformPlayers(resolved: ResolvedBlock | null) {
	if (!resolved) return []
	const validPlayers = resolved.players.filter(
		(p): p is PlayerBlock => p != null && p.properties != null
	)
	return playerBlocksToPlayers(validPlayers)
}

/**
 * Transform ResolvedDrawing[] to Drawing[] for canvas rendering.
 * Uses the adapter layer for proper type conversion.
 */
function transformDrawings(resolved: ResolvedBlock | null) {
	if (!resolved) return []
	return drawingBlocksToDrawings(resolved.drawings)
}

/**
 * usePlayBlocks Hook
 *
 * Manages play state with React Query for server state and local state for UI.
 */
export function usePlayBlocks(options: UsePlayBlocksOptions): UsePlayBlocksReturn {
	const { playBlockId, playbookId, enabled = true } = options
	const queryClient = useQueryClient()

	// ========== Local UI State ==========
	const [drawingState, setDrawingStateInternal] = useState<DrawingState>(DEFAULT_DRAWING_STATE)
	const [hashAlignment, setHashAlignmentInternal] = useState<HashAlignment>(DEFAULT_HASH_ALIGNMENT)

	// ========== React Query ==========
	const shouldFetch = playBlockId !== null && enabled

	const {
		data: resolvedBlock,
		isLoading,
		error,
	} = useQuery({
		queryKey: getResolvedBlockQueryKey(playBlockId),
		queryFn: () => fetchResolvedBlock(playBlockId!),
		enabled: shouldFetch,
		staleTime: 30000,
		refetchOnWindowFocus: false,
	})

	// ========== Cache Getter for Mutations ==========
	// Provides access to cached ResolvedBlock data for undo/redo operations.
	// Using a getter ensures mutations always access the latest cached state.
	const getCachedPlay = useCallback((): ResolvedBlock | null => {
		return queryClient.getQueryData<ResolvedBlock>(getResolvedBlockQueryKey(playBlockId)) ?? null
	}, [playBlockId, queryClient])

	// ========== Mutations ==========
	// PERFORMANCE: Mutations use getCachedPlay to read original values from cache
	// instead of fetching before each mutation. This eliminates ~50% of HTTP requests.
	const mutations = usePlayMutations({
		playBlockId,
		playbookId,
		getCachedPlay,
	})

	// ========== State Updaters ==========
	const setDrawingState = useCallback((updates: Partial<DrawingState>) => {
		setDrawingStateInternal((prev) => ({ ...prev, ...updates }))
	}, [])

	const setHashAlignment = useCallback((alignment: HashAlignment) => {
		setHashAlignmentInternal(alignment)
	}, [])

	// ========== Refetch ==========
	const refetch = useCallback(() => {
		if (playBlockId !== null) {
			queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		}
	}, [playBlockId, queryClient])

	// ========== Undo/Redo from Context ==========
	const {
		undo: undoFromContext,
		redo: redoFromContext,
		canUndo,
		canRedo,
	} = useUndo()

	const undo = useCallback(async () => {
		await undoFromContext()
		if (playBlockId !== null) {
			await queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		}
	}, [undoFromContext, playBlockId, queryClient])

	const redo = useCallback(async () => {
		await redoFromContext()
		if (playBlockId !== null) {
			await queryClient.invalidateQueries({ queryKey: getResolvedBlockQueryKey(playBlockId) })
		}
	}, [redoFromContext, playBlockId, queryClient])

	// ========== Derived Data ==========
	const players = useMemo(
		() => transformPlayers(resolvedBlock ?? null),
		[resolvedBlock]
	)

	const drawings = useMemo(
		() => transformDrawings(resolvedBlock ?? null),
		[resolvedBlock]
	)

	const appliedFormation = resolvedBlock?.formationBlock ?? null
	const appliedConcepts = resolvedBlock?.conceptReferences ?? []

	// ========== Return ==========
	return {
		// Server state
		resolvedBlock: resolvedBlock ?? null,
		isLoading: shouldFetch ? isLoading : false,
		error: error instanceof Error ? error : null,

		// Transformed canvas data
		players,
		drawings,

		// Local UI state
		drawingState,
		setDrawingState,
		hashAlignment,
		setHashAlignment,

		// Mutations placeholder
		mutations,

		// Undo/redo from context
		undo,
		redo,
		canUndo,
		canRedo,

		// Applied references
		appliedFormation,
		appliedConcepts,

		// Refresh
		refetch,
	}
}
